import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import {
  listTemplates,
  listFormPackets,
  createFormPacket,
  getFormPacket
} from '../../services/formsApi';
import TemplatePreview from './TemplatePreview';

const PACKET_STATUS_COLORS = {
  draft: 'default',
  queued: 'info',
  sent: 'primary',
  in_progress: 'warning',
  completed: 'success',
  declined: 'error',
  expired: 'default'
};

const SIGNER_STATUS_COLORS = {
  pending: 'default',
  invited: 'info',
  viewed: 'warning',
  signed: 'success',
  completed: 'success',
  declined: 'error'
};

const AUTH_METHOD_OPTIONS = [
  { value: 'email_otp', label: 'Email OTP' },
  { value: 'sms_otp', label: 'SMS OTP' },
  { value: 'pin', label: 'PIN' },
  { value: 'none', label: 'None' }
];

const humanizeEventType = (value) => {
  if (!value) return '';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const formatDateOnly = (value) => {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleDateString();
  } catch (error) {
    return value;
  }
};

const createEmptySigner = () => ({
  name: '',
  email: '',
  authMethod: 'email_otp'
});

const PacketPanel = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [templates, setTemplates] = useState([]);
  const [packets, setPackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [title, setTitle] = useState('');
  const [dueInDays, setDueInDays] = useState('7');
  const [note, setNote] = useState('');
  const [signers, setSigners] = useState([createEmptySigner()]);
  const [sending, setSending] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPacket, setDetailPacket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const templatesById = useMemo(() => {
    const map = new Map();
    templates.forEach((template) => {
      map.set(Number(template.id), template);
    });
    return map;
  }, [templates]);

  const primaryTemplateId = useMemo(() => {
    if (selectedTemplateIds.length === 0) {
      return null;
    }
    return Number(selectedTemplateIds[0]);
  }, [selectedTemplateIds]);

  const selectedTemplateObjects = useMemo(
    () =>
      selectedTemplateIds
        .map((id) => templatesById.get(Number(id)))
        .filter(Boolean),
    [selectedTemplateIds, templatesById]
  );

  const statusCounts = useMemo(() => {
    return packets.reduce(
      (acc, packet) => {
        acc.total += 1;
        acc[packet.status] = (acc[packet.status] || 0) + 1;
        return acc;
      },
      { total: 0 }
    );
  }, [packets]);

  const activeSignerCount = useMemo(
    () =>
      signers.filter((signer) => (signer.email || '').trim().length > 0).length,
    [signers]
  );

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templateData, packetData] = await Promise.all([
        listTemplates(),
        listFormPackets()
      ]);
      setTemplates(templateData);
      setPackets(packetData);
      setSelectedTemplateIds((prev) => {
        if (prev.length === 0 && templateData.length > 0) {
          return [String(templateData[0].id)];
        }
        const validIds = prev.filter((id) =>
          templateData.some((template) => String(template.id) === id)
        );
        if (validIds.length > 0) {
          return validIds;
        }
        return templateData.length > 0 ? [String(templateData[0].id)] : [];
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to load packet data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateChange = (event) => {
    const { value } = event.target;
    const next = typeof value === 'string' ? value.split(',') : value;
    setSelectedTemplateIds(next);
  };

  const handleSignerFieldChange = (index, field) => (event) => {
    const { value } = event.target;
    setSigners((prev) =>
      prev.map((signer, idx) =>
        idx === index ? { ...signer, [field]: value } : signer
      )
    );
  };

  const handleAddSigner = () => {
    setSigners((prev) => [...prev, createEmptySigner()]);
  };

  const handleRemoveSigner = (index) => {
    setSigners((prev) => {
      if (prev.length === 1) {
        return prev;
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const handleCreatePacket = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage('');

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Packet title is required.');
      return;
    }

    if (!primaryTemplateId) {
      setError('Select at least one template to include in the packet.');
      return;
    }

    const preparedSigners = signers
      .map((signer) => ({
        name: (signer.name || '').trim(),
        email: (signer.email || '').trim(),
        authMethod: signer.authMethod || 'email_otp'
      }))
      .filter((signer) => signer.email.length > 0);

    if (preparedSigners.length === 0) {
      setError('Add at least one signer with an email address.');
      return;
    }

    const hasDue =
      dueInDays !== '' && !Number.isNaN(Number(dueInDays));
    if (hasDue && Number(dueInDays) < 0) {
      setError('Due in (days) must be zero or greater.');
      return;
    }
    const dueInDaysNumber = hasDue ? Number(dueInDays) : null;
    const dueDateIso = hasDue
      ? new Date(
          Date.now() + dueInDaysNumber * 24 * 60 * 60 * 1000
        ).toISOString()
      : null;

    const normalisedSigners = preparedSigners.map((signer, index) => ({
      signer_order: index + 1,
      signer_type: 'external',
      external_name: signer.name || signer.email,
      external_email: signer.email,
      auth_method: signer.authMethod,
      status: 'invited'
    }));

    const templateIdsNumeric = selectedTemplateIds.map((id) => Number(id));
    const metadata = {};
    if (templateIdsNumeric.length > 0) {
      metadata.template_ids = templateIdsNumeric;
      metadata.primary_template_id = templateIdsNumeric[0];
      if (templateIdsNumeric.length > 1) {
        metadata.additional_template_ids = templateIdsNumeric.slice(1);
      }
    }
    if (hasDue) {
      metadata.due_in_days = dueInDaysNumber;
    }
    if (note.trim()) {
      metadata.memo = note.trim();
    }
    metadata.signer_count = normalisedSigners.length;

    const metadataPayload =
      Object.keys(metadata).length > 0 ? metadata : null;

    setSending(true);
    try {
      await createFormPacket({
        template_id: primaryTemplateId,
        title: trimmedTitle,
        status: 'sent',
        due_date: dueDateIso,
        metadata: metadataPayload,
        signers: normalisedSigners
      });

      setSuccessMessage(
        `Packet sent to ${normalisedSigners.length} signer${
          normalisedSigners.length === 1 ? '' : 's'
        }.`
      );
      setTitle('');
      setNote('');
      setDueInDays('7');
      setSigners([createEmptySigner()]);
      if (templates.length > 0) {
        setSelectedTemplateIds([String(templates[0].id)]);
      } else {
        setSelectedTemplateIds([]);
      }
      await loadData();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to send packet');
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const openPacketDetail = async (packetId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const packet = await getFormPacket(packetId);
      setDetailPacket(packet);
    } catch (err) {
      console.error(err);
      setDetailError(err.message || 'Unable to load packet details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closePacketDetail = () => {
    setDetailOpen(false);
    setDetailPacket(null);
    setDetailError(null);
  };

  const initialising = loading && templates.length === 0 && packets.length === 0;

  if (initialising) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(41,55,85,0.12)' }}>
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          <Box flex={2}>
            <Typography variant="h6" gutterBottom>
              Compose Packet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 680 }}>
              Bundle multiple templates into a single packet, add your signers, and send a secure
              link that captures signatures with an auditable trail.
            </Typography>

            <Stack component="form" spacing={2.5} onSubmit={handleCreatePacket}>
              <TextField
                label="Packet Title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                required
                placeholder="e.g. Onboarding Packet – Alex Jordan"
              />

              <TextField
                select
                label="Templates"
                value={selectedTemplateIds}
                onChange={handleTemplateChange}
                required
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    if (!selected || selected.length === 0) {
                      return 'Select templates';
                    }
                    const names = selected
                      .map((id) => templatesById.get(Number(id))?.name || `Template ${id}`)
                      .join(', ');
                    return names;
                  }
                }}
                helperText="Select one or more forms to include. The first selection becomes the primary document."
              >
                {templates.map((template) => {
                  const templateId = String(template.id);
                  return (
                    <MenuItem key={template.id} value={templateId}>
                      <Checkbox checked={selectedTemplateIds.indexOf(templateId) > -1} />
                      <ListItemText
                        primary={template.name}
                        secondary={template.template_type === 'pdf' ? 'PDF template' : 'Builder template'}
                      />
                    </MenuItem>
                  );
                })}
              </TextField>

              {selectedTemplateObjects.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {selectedTemplateObjects.map((template, index) => (
                    <Chip
                      key={template.id}
                      label={`${template.name}${index === 0 ? ' (Primary)' : ''}`}
                      icon={<DescriptionIcon fontSize="small" />}
                      variant="outlined"
                      onClick={() => setPreviewTemplate(template)}
                      clickable
                    />
                  ))}
                </Stack>
              )}

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Due in (days)"
                  type="number"
                  value={dueInDays}
                  onChange={(event) => setDueInDays(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ScheduleIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ maxWidth: 200 }}
                  placeholder="7"
                />
                <TextField
                  label="Internal note (optional)"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Stack>

              <Stack spacing={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Signers
                </Typography>
                {signers.map((signer, index) => (
                  <Paper key={`signer-${index}`} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2" color="text.secondary">
                          Signer {index + 1}
                        </Typography>
                        {signers.length > 1 && (
                          <Tooltip title="Remove signer">
                            <IconButton size="small" onClick={() => handleRemoveSigner(index)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          label="Signer name"
                          placeholder="Full name"
                          value={signer.name}
                          onChange={handleSignerFieldChange(index, 'name')}
                          fullWidth
                          required
                        />
                        <TextField
                          label="Signer email"
                          type="email"
                          placeholder="signer@example.com"
                          value={signer.email}
                          onChange={handleSignerFieldChange(index, 'email')}
                          required
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MailOutlineIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Stack>
                      <TextField
                        label="Authentication method"
                        select
                        value={signer.authMethod}
                        onChange={handleSignerFieldChange(index, 'authMethod')}
                        sx={{ maxWidth: 260 }}
                      >
                        {AUTH_METHOD_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Paper>
                ))}
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  variant="outlined"
                  onClick={handleAddSigner}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add signer
                </Button>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SendIcon />}
                  disabled={sending || templates.length === 0}
                >
                  {sending ? 'Sending…' : 'Send packet'}
                </Button>
                <Tooltip title="Refresh templates and packets">
                  <IconButton onClick={handleRefresh}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Preparing {selectedTemplateIds.length || 0} template
                {selectedTemplateIds.length === 1 ? '' : 's'} for {activeSignerCount}{' '}
                signer{activeSignerCount === 1 ? '' : 's'}.
              </Typography>
            </Stack>
          </Box>

          <Box flex={1}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff, #f6f8fb)'
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Status Spotlight
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {statusCounts.total || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total packets
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 600 }}>
                      {(statusCounts.sent || 0) + (statusCounts.in_progress || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active deliveries
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="h5" color="success.main" sx={{ fontWeight: 600 }}>
                      {statusCounts.completed || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="h5" color="error.main" sx={{ fontWeight: 600 }}>
                      {(statusCounts.declined || 0) + (statusCounts.expired || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Attention needed
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Each packet delivers a secure signing link and records every action for auditing,
                just like DocuSign—without leaving Dailey Forms.
              </Typography>
            </Paper>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(41,55,85,0.12)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Recent Packets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track signer progress, review audit events, and revisit generated documents.
            </Typography>
          </Box>
          <Tooltip title="Reload packets">
            <span>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {packets.length === 0 ? (
          <Box
            sx={{
              py: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No packets yet. Compose your first packet above.
              </Typography>
            )}
          </Box>
        ) : isSmallScreen ? (
          <Stack spacing={2}>
            {packets.map((packet) => {
              const templateIds =
                Array.isArray(packet.metadata?.template_ids) && packet.metadata.template_ids.length > 0
                  ? packet.metadata.template_ids
                  : packet.template_id
                  ? [packet.template_id]
                  : [];
              const templateLabels = templateIds.map((id) => {
                const template = templatesById.get(Number(id));
                return template ? template.name : `Template ${id}`;
              });
              const primaryTemplate =
                templatesById.get(Number(templateIds[0])) ||
                templatesById.get(Number(packet.template_id));

              return (
                <Paper key={packet.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {packet.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated {formatDateTime(packet.updated_at || packet.updatedAt)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={packet.status?.toUpperCase() || 'UNKNOWN'}
                        color={PACKET_STATUS_COLORS[packet.status] || 'default'}
                      />
                      {packet.due_date && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Due ${formatDateOnly(packet.due_date)}`}
                        />
                      )}
                    </Stack>
                    {templateLabels.length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {templateLabels.map((label, index) => (
                          <Chip
                            key={`${packet.id}-tpl-card-${index}`}
                            size="small"
                            label={label}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    )}
                    <Stack spacing={1}>
                      {packet.signers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No signers assigned
                        </Typography>
                      ) : (
                        packet.signers.map((signer) => (
                          <Stack
                            key={signer.id || signer.external_email}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Chip
                              size="small"
                              label={signer.status?.toUpperCase() || 'UNKNOWN'}
                              color={SIGNER_STATUS_COLORS[signer.status] || 'default'}
                            />
                            <Typography variant="body2">
                              {signer.external_name || signer.external_email}
                            </Typography>
                          </Stack>
                        ))
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={() => openPacketDetail(packet.id)}
                      >
                        Details
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DescriptionIcon fontSize="small" />}
                        onClick={() => primaryTemplate && setPreviewTemplate(primaryTemplate)}
                        disabled={!primaryTemplate}
                      >
                        Template
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Packet</TableCell>
                  <TableCell>Signers</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Due</TableCell>
                  <TableCell align="center">Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packets.map((packet) => {
                  const templateIds =
                    Array.isArray(packet.metadata?.template_ids) &&
                    packet.metadata.template_ids.length > 0
                      ? packet.metadata.template_ids
                      : packet.template_id
                      ? [packet.template_id]
                      : [];
                  const templateLabels = templateIds.map((id) => {
                    const template = templatesById.get(Number(id));
                    return template ? template.name : `Template ${id}`;
                  });
                  const primaryTemplate =
                    templatesById.get(Number(templateIds[0])) ||
                    templatesById.get(Number(packet.template_id));

                  return (
                    <TableRow key={packet.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {packet.title}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {templateLabels.map((label, index) => (
                            <Chip
                              key={`${packet.id}-tpl-${index}`}
                              size="small"
                              label={label}
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {packet.signers.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No signers assigned
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {packet.signers.map((signer) => (
                              <Chip
                                key={signer.id || signer.external_email}
                                label={`${signer.external_name || signer.external_email} · ${
                                  signer.status?.toUpperCase() || 'UNKNOWN'
                                }`}
                                size="small"
                                color={SIGNER_STATUS_COLORS[signer.status] || 'default'}
                              />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={packet.status?.toUpperCase() || 'UNKNOWN'}
                          color={PACKET_STATUS_COLORS[packet.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">{formatDateOnly(packet.due_date)}</TableCell>
                      <TableCell align="center">
                        {formatDateTime(packet.updated_at || packet.updatedAt)}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View packet details">
                            <IconButton size="small" onClick={() => openPacketDetail(packet.id)}>
                              <VisibilityIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Preview primary template">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => primaryTemplate && setPreviewTemplate(primaryTemplate)}
                                disabled={!primaryTemplate}
                              >
                                <DescriptionIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={detailOpen} onClose={closePacketDetail} fullWidth maxWidth="md">
        <DialogTitle>Packet Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 180
              }}
            >
              <CircularProgress />
            </Box>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : detailPacket ? (
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Typography variant="h6">{detailPacket.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {detailPacket.status?.toUpperCase() || 'UNKNOWN'} · Created{' '}
                  {formatDateTime(detailPacket.created_at)}
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body2">{formatDateTime(detailPacket.due_date)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Sent
                  </Typography>
                  <Typography variant="body2">{formatDateTime(detailPacket.sent_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body2">
                    {detailPacket.completed_at ? formatDateTime(detailPacket.completed_at) : '—'}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Templates
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(Array.isArray(detailPacket.metadata?.template_ids) &&
                  detailPacket.metadata.template_ids.length > 0
                    ? detailPacket.metadata.template_ids
                    : detailPacket.template_id
                    ? [detailPacket.template_id]
                    : []
                  ).map((id) => {
                    const template = templatesById.get(Number(id));
                    return (
                      <Chip
                        key={`detail-template-${id}`}
                        size="small"
                        label={template ? template.name : `Template ${id}`}
                        icon={<DescriptionIcon fontSize="small" />}
                        variant="outlined"
                      />
                    );
                  })}
                </Stack>
              </Stack>

              {detailPacket.metadata?.memo && (
                <Alert severity="info">{detailPacket.metadata.memo}</Alert>
              )}

              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle1">Signers</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Signer</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Auth</TableCell>
                      <TableCell>Invited</TableCell>
                      <TableCell>Completed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailPacket.signers.map((signer) => (
                      <TableRow key={signer.id || signer.external_email}>
                        <TableCell>
                          <Typography variant="body2">{signer.external_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {signer.external_email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={signer.status?.toUpperCase() || 'UNKNOWN'}
                            color={SIGNER_STATUS_COLORS[signer.status] || 'default'}
                          />
                        </TableCell>
                        <TableCell>{signer.auth_method?.toUpperCase() || '—'}</TableCell>
                        <TableCell>{formatDateTime(signer.invited_at)}</TableCell>
                        <TableCell>{formatDateTime(signer.completed_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>

              {detailPacket.documents && detailPacket.documents.length > 0 && (
                <>
                  <Divider />
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">Generated Documents</Typography>
                    <Stack spacing={0.5}>
                      {detailPacket.documents.map((doc) => (
                        <Typography key={doc.id} variant="body2">
                          {doc.generated_pdf_path || 'Pending'} ·{' '}
                          {doc.finalized_at
                            ? `Finalized ${formatDateTime(doc.finalized_at)}`
                            : 'Awaiting completion'}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>
                </>
              )}

              {detailPacket.events && detailPacket.events.length > 0 && (
                <>
                  <Divider />
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">Event Timeline</Typography>
                    <Stack spacing={1}>
                      {detailPacket.events.map((event) => {
                        const signer =
                          detailPacket.signers.find((s) => s.id === event.signer_id) || null;
                        const detailEntries =
                          event.event_details && typeof event.event_details === 'object'
                            ? Object.entries(event.event_details)
                            : [];
                        return (
                          <Box key={event.id} sx={{ borderLeft: '2px solid rgba(13,36,65,0.12)', pl: 2 }}>
                            <Typography variant="body2">
                              <strong>{formatDateTime(event.created_at)}</strong> ·{' '}
                              {humanizeEventType(event.event_type)}
                              {signer ? ` (${signer.external_name || signer.external_email})` : ''}
                            </Typography>
                            {detailEntries.length > 0 && (
                              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                {detailEntries.map(([key, value]) => (
                                  <Chip
                                    key={`${event.id}-${key}`}
                                    size="small"
                                    variant="outlined"
                                    label={`${key}: ${String(value)}`}
                                  />
                                ))}
                              </Stack>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a packet to view details.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePacketDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {previewTemplate && (
        <TemplatePreview
          open={Boolean(previewTemplate)}
          onClose={() => setPreviewTemplate(null)}
          template={previewTemplate}
        />
      )}
    </Stack>
  );
};

export default PacketPanel;
